import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { RefreshCw, Search } from 'lucide-react'
import { KB_STAGES, type SolutionListItem } from '@loop3/universe'
import {
  Button,
  cn,
  combineQueryStates,
  EmptyView,
  FilterChip,
  FilterSelect,
  Input,
  QueryErrorPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  Tag,
  ViewToggle,
} from '@loop3/ui'
import { useDebouncedSearchParam } from '@/hooks/useDebouncedSearchParam'
import { useKbRefresh } from '@/hooks/useKbRefresh'
import { useSolutionsLibrary } from '@/hooks/useSolutionsLibrary'
import {
  facetOptions,
  filterSolutions,
  FILTER_PARAMS,
  hasFilters,
  readFilters,
  syncFreshness,
  writeFilter,
  type LibraryFilters,
} from '@/lib/compute/solutions-library'
import { solutionPath } from '@/lib/routes'

const EMPTY: SolutionListItem[] = []

const TYPE_CHIPS: { value: LibraryFilters['type']; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'solution', label: 'Solutions' },
  { value: 'feature', label: 'Features' },
]

/** Searchable, filterable table of every live solution and feature, bound to the URL. */
export function SolutionsLibrary() {
  const [params, setParams] = useSearchParams()
  const library = useSolutionsLibrary()
  const refresh = useKbRefresh()
  const refreshing = refresh.state === 'starting' || refresh.state === 'waiting'
  const filters = readFilters(params)

  const set = (key: keyof LibraryFilters, value: string | boolean | null) =>
    setParams((prev) => writeFilter(prev, key, value), { replace: true })
  const clear = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        FILTER_PARAMS.forEach((k) => next.delete(k))
        return next
      },
      { replace: true }
    )
  const text = useDebouncedSearchParam(filters.q, (next) => set('q', next))

  const all = library.data ?? EMPTY
  const rows = filterSolutions(all, filters)
  const facets = useMemo(() => facetOptions(all), [all])
  const { status, retry } = combineQueryStates([library])
  const filtered = hasFilters(filters)
  const clearButton = (
    <Button variant="ghost" size="sm" onClick={clear}>
      Clear filters
    </Button>
  )

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 opacity-40"
            />
            <Input
              type="search"
              aria-label="Search solutions"
              placeholder="Search solutions, clients or capabilities"
              value={text.value}
              onChange={(e) => text.set(e.target.value)}
              className="h-10 rounded-lg bg-card pl-9 text-sm md:text-sm"
            />
          </div>
          <ViewToggle
            ariaLabel="Type"
            options={TYPE_CHIPS}
            value={filters.type}
            onChange={(v) => set('type', v)}
            className="h-10 shrink-0 self-start p-1 sm:self-auto"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <FilterSelect
            label="Client"
            allLabel="All clients"
            value={filters.client}
            options={facets.clients.map((c) => ({
              value: c.prefix,
              label: c.name,
            }))}
            onChange={(v) => set('client', v)}
          />
          <FilterSelect
            label="Vertical"
            allLabel="All verticals"
            value={filters.vertical}
            options={facets.verticals.map((v) => ({ value: v, label: v }))}
            onChange={(v) => set('vertical', v)}
          />
          <FilterSelect
            label="Capability"
            allLabel="All capabilities"
            value={filters.capability}
            options={facets.capabilities.map((v) => ({ value: v, label: v }))}
            onChange={(v) => set('capability', v)}
          />
          <FilterSelect
            label="Stage"
            allLabel="All stages"
            value={filters.stage}
            options={KB_STAGES.map((v) => ({ value: v, label: v }))}
            onChange={(v) => set('stage', v)}
          />
          <FilterSelect
            label="Lifecycle"
            allLabel="All lifecycles"
            value={filters.lifecycle}
            options={facets.lifecycles.map((v) => ({ value: v, label: v }))}
            onChange={(v) => set('lifecycle', v)}
          />
          <FilterChip
            active={filters.reusable}
            onClick={() => set('reusable', !filters.reusable)}
            className="h-8"
          >
            Reusable only
          </FilterChip>
          {filtered && clearButton}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {status === 'ready' &&
            countLabel(rows.length, all.length, filters.type, filtered)}
        </span>
        <span className="flex flex-wrap items-center gap-x-2">
          <Freshness finishedAt={refresh.lastSync} ready={refresh.syncReady} />
          <span aria-live="polite">
            {refresh.error ??
              (refresh.state === 'timed-out' &&
                'The last refresh did not finish. Try again in a minute.')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={refreshing || !refresh.syncReady}
            onClick={refresh.refresh}
          >
            <RefreshCw
              aria-hidden
              className={cn('size-3.5', refreshing && 'animate-spin')}
            />
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </Button>
        </span>
      </div>

      <div className="border-t border-border">
        {status === 'error' ? (
          <QueryErrorPanel onRetry={retry} />
        ) : status === 'pending' ? (
          <TableSkeleton rows={6} cols={4} firstColAvatar={false} />
        ) : rows.length === 0 ? (
          <EmptyView
            className="min-h-[20vh] py-8"
            title="No solutions match"
            action={filtered ? clearButton : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="hidden md:table-cell">Stages</TableHead>
                  <TableHead>Lifecycle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Link
                        to={solutionPath(s.id)}
                        className="text-sm transition-colors hover:text-[var(--l3-violet)]"
                      >
                        {s.name}
                      </Link>
                      {s.type === 'feature' && (
                        <Tag variant="muted" className="ml-2">
                          Feature
                        </Tag>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.client ? (
                        <Link
                          to={`/clients/${s.client.prefix}`}
                          className="text-xs hover:text-[var(--l3-violet)] hover:underline"
                        >
                          {s.client.name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {s.stages.length ? (
                        <div className="flex flex-wrap gap-1">
                          {s.stages.map((st) => (
                            <Tag key={st}>{st}</Tag>
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.lifecycle ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </section>
  )
}

function Freshness({
  finishedAt,
  ready,
}: {
  finishedAt: string | null
  ready: boolean
}) {
  const fresh = ready ? syncFreshness(finishedAt, new Date()) : null
  if (!fresh) return null
  return (
    <span>
      {`Last updated ${fresh.label}`}
      {fresh.stale && (
        <span className="text-amber-600"> · May be out of date</span>
      )}
    </span>
  )
}

const NOUNS: Record<LibraryFilters['type'], [string, string]> = {
  all: ['entry', 'entries'],
  solution: ['solution', 'solutions'],
  feature: ['feature', 'features'],
}

/** "12 features of 184" when filtered, "184 entries" otherwise. */
function countLabel(
  shown: number,
  total: number,
  type: LibraryFilters['type'],
  filtered: boolean
): string {
  const [one, many] = NOUNS[type]
  return `${shown} ${shown === 1 ? one : many}${filtered ? ` of ${total}` : ''}`
}
